/**
 * Format inference, format conformity, and PII classification.
 *
 * ⚠️ **Both use the header name AND the values, and the values win.** A column called `email` full
 * of account numbers is account numbers; a column called `col_7` full of `a@b.com` is email. Header
 * names in a CRM export are whatever a person typed years ago, so treating them as authoritative is
 * how a profiler ends up confidently describing data it never looked at.
 *
 * The header is not ignored either — it is the only evidence available for a sparse column, and it
 * is the difference between `mobile` and `extension` when both hold digits.
 */
import type { FormatKind, FormatProfile, PiiClass } from './model.js';

/* ── Shape tests ───────────────────────────────────────────────────────────────────────────
   Deliberately permissive. A strict RFC 5322 email regex rejects addresses that deliver fine, and
   every rejection here becomes a "problem" a reader has to dismiss. */

const EMAIL = /^[^\s@]+@[^\s@.]+\.[^\s@]+$/;
/** 7+ digits after stripping the punctuation phone numbers are legitimately written with. */
const PHONE = /^\+?[\d]{7,15}$/;
const URL = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/\S*)?$/i;
const NUMBER = /^-?\d+(\.\d+)?$/;
/**
 * ⚠️ **A date must LOOK like one before `Date.parse` is consulted.**
 *
 * `Date.parse` is far more lenient than it appears: `Date.parse('unique-0')` returns a valid
 * timestamp in the year 2000. Trusting it alone made arbitrary text profile as dates — and a field
 * classified `date` is weighted 0.1 as a matching key, so a genuine identifier column would have
 * been silently ranked last. Found by a test whose expectation was right for a reason I had not
 * guessed.
 */
const DATE_SHAPE =
  /^\d{4}-\d{1,2}-\d{1,2}([T ].*)?$|^\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}([T ].*)?$|^\d{1,2}\s+[A-Za-z]{3,}\s+\d{2,4}$|^[A-Za-z]{3,}\s+\d{1,2},?\s+\d{2,4}$/;

const MATCHERS: Record<FormatKind, (value: string) => boolean> = {
  email: (v) => EMAIL.test(v),
  phone: (v) => PHONE.test(v.replace(/[\s()\-./]/g, '')),
  url: (v) => URL.test(v),
  number: (v) => NUMBER.test(v.replace(/[\s,]/g, '')),
  /* Shape first, `Date.parse` second. Either alone is wrong: the shape test would accept
     `2026-99-99`, and `Date.parse` alone accepts `unique-0`. */
  date: (v) => DATE_SHAPE.test(v.trim()) && !Number.isNaN(Date.parse(v.trim())),
};

/**
 * Header names arrive as `job_title`, `firstName`, `Company-Name`. `\b` does **not** match around an
 * underscore — `_` is a word character — so `\bjob\b` never matched `job_title`, and every hint and
 * PII rule silently failed on the naming convention CRM exports use most.
 *
 * Normalising to spaced words first makes the word-boundary patterns mean what they read as. Found
 * by a test asserting `job_title` is indirect PII, which returned `none`.
 */
export function words(field: string): string {
  return field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .trim();
}

/** Header-name hints, checked as whole words so `company_id` does not read as `id`. */
const HEADER_HINTS: { kind: FormatKind; pattern: RegExp }[] = [
  { kind: 'email', pattern: /\b(e-?mail|mail)\b/i },
  { kind: 'phone', pattern: /\b(phone|mobile|tel|telephone|fax|cell)\b/i },
  { kind: 'url', pattern: /\b(url|website|site|web|domain|link)\b/i },
  { kind: 'date', pattern: /\b(date|created|updated|modified|at|on|birthday|dob)\b/i },
];

/**
 * Which format, if any, this column is trying to be.
 *
 * Values first: a format is inferred when **at least 60% of present values** match it. Below that
 * the header decides, and only if the header hints at something. Both silent → `null`, which is the
 * honest answer for a free-text column and by far the most common one.
 *
 * 60% rather than a majority: a real export routinely has a tenth of its emails as `n/a` or a
 * name, and a 50% threshold flips between two formats on noise.
 */
export function inferFormat(field: string, values: string[]): FormatKind | null {
  if (values.length === 0) {
    const hinted = HEADER_HINTS.find((h) => h.pattern.test(words(field)));
    return hinted?.kind ?? null;
  }

  /* ⚠️ **Most specific first, and this ordering is load-bearing rather than cosmetic — the formats
     OVERLAP.** `phone` strips the punctuation phone numbers are legitimately written with, so
     `2026-01-01` becomes `20260101`: eight digits, a perfectly good phone number by shape. A
     created-date column therefore profiled as `phone` and was ranked a *strong* matching key
     (weight 0.7) instead of a weak one (0.1). Picking the highest share cannot resolve that — both
     match 100% of the values — so specificity decides, and `date` is checked first.
     `number` is last for the same reason: every digit string matches it. */
  const PRIORITY: FormatKind[] = ['email', 'url', 'date', 'phone', 'number'];
  let best: { kind: FormatKind; share: number } | null = null;
  for (const kind of PRIORITY) {
    const matching = values.filter((v) => MATCHERS[kind](v)).length;
    const share = matching / values.length;
    if (share >= 0.6) {
      best = { kind, share };
      break;
    }
  }
  /* Digits match `number` AND `phone`, and a column of phone numbers described as "number" tells
     the reader nothing they can act on — so a `phone` header wins over a `number` inference.
     ⚠️ **Only `phone`.** An earlier version let ANY header hint override, so a column called
     `email` full of account numbers profiled as `email` — the header beating the values, which is
     the exact inversion this function's docblock forbids. */
  if (best && best.kind === 'number') {
    const hinted = HEADER_HINTS.find((h) => h.pattern.test(words(field)));
    if (hinted?.kind === 'phone') return 'phone';
  }
  if (best) return best.kind;

  return HEADER_HINTS.find((h) => h.pattern.test(words(field)))?.kind ?? null;
}

/** Conformity against an inferred format, with the non-conforming values attached as evidence. */
export function profileFormat(kind: FormatKind, values: string[]): FormatProfile {
  const matcher = MATCHERS[kind];
  const failures: string[] = [];
  let conforming = 0;
  for (const value of values) {
    if (matcher(value)) conforming += 1;
    else if (failures.length < 5) failures.push(value);
  }
  return {
    kind,
    conforming,
    // 1 for an empty column: nothing failed. 0 would read as "everything is wrong".
    conformity: values.length === 0 ? 1 : conforming / values.length,
    examples: failures,
  };
}

/* ── PII ───────────────────────────────────────────────────────────────────────────────────
   Header patterns first, because a name column is a name column whatever its values look like, and
   the value shapes cannot recognise a person's name at all. */

const DIRECT_HEADERS =
  /\b(e-?mail|mail|phone|mobile|tel|telephone|fax|cell|name|firstname|lastname|surname|forename|ssn|nino|passport|national\s*id|address|street|postcode|zip)\b/i;
const INDIRECT_HEADERS =
  /\b(company|organisation|organization|account|employer|title|role|job|department|city|town|county|state|country|region|industry|ip)\b/i;
/** Explicitly not personal, even when the header contains a word that looks like it. */
const NON_PII_HEADERS = /\b(count|total|amount|score|rank|status|stage|type|flag|is[_\s]|has[_\s])\b/i;

/**
 * Classify a field's sensitivity, with the reason in words.
 *
 * ⚠️ **Advisory, and it must be presented as such.** This is a heuristic over a header name and a
 * sample of values; a custom field called `notes` holding home addresses classifies as `none` and
 * is plainly personal data. It exists to make the *common* cases automatic and to give a person
 * something to correct — never to be the thing a masking decision rests on unreviewed.
 */
export function classifyPii(
  field: string,
  values: string[],
  format: FormatKind | null
): { pii: PiiClass; reason: string; certain: boolean } {
  if (format === 'email') {
    return { pii: 'direct', reason: 'Holds email addresses, which identify a person on their own.', certain: true };
  }
  if (format === 'phone') {
    return { pii: 'direct', reason: 'Holds phone numbers, which identify a person on their own.', certain: true };
  }

  const name = words(field);
  if (NON_PII_HEADERS.test(name)) {
    return { pii: 'none', reason: 'Reads as a measure or a status rather than something about a person.', certain: true };
  }
  /* ⚠️ **Company context beats the bare word "name".** `Company Name`, `Account Name` and
     `Organisation Name` all contain `name`, and `DIRECT_HEADERS` matched them before the indirect
     rule was ever reached — so the most common company column in any CRM export was flagged as
     directly identifying a person. Wrong on its face to a reader ("why is my company column
     personal?"), and it erodes trust in every other classification on the screen. Caught by looking
     at a real export in a browser, not by any test. */
  if (/\b(company|organisation|organization|account|employer)\b/i.test(name)) {
    return {
      pii: 'indirect',
      reason: `The name "${field}" describes an organisation, which identifies a person only in combination with other fields.`,
      certain: true,
    };
  }
  if (DIRECT_HEADERS.test(name)) {
    return { pii: 'direct', reason: `The name "${field}" describes something that identifies a person on its own.`, certain: true };
  }
  if (INDIRECT_HEADERS.test(name)) {
    return {
      pii: 'indirect',
      reason: `The name "${field}" describes something that identifies a person in combination with other fields.`,
      certain: true,
    };
  }

  /* Nothing in the header, so fall back to shape. A column of email-looking values in a column
     called `col_7` is still email addresses. */
  if (values.length > 0 && values.filter((v) => EMAIL.test(v)).length / values.length >= 0.6) {
    return { pii: 'direct', reason: 'Most values look like email addresses, whatever the column is called.', certain: true };
  }

  /* ⚠️ The ONE uncertain exit — and until 2026-08-05 it returned a quiet `none`, which is
     precisely what M3's BR-2 forbids ("uncertain → flagged for human confirmation, never silently
     `none`"). `certain: false` is what routes this residue to the LLM seam / the review queue. */
  return {
    pii: 'none',
    reason: 'Nothing in the column name or its values indicates personal data — worth confirming by eye.',
    certain: false,
  };
}
